import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { PromotionInputs } from "@/pages/Promotions/PromotionModal/PromotionModal";
import { ApiRetailer } from "@api/interfaces";
import { ProductList } from "@/components/shared/ListSelector/ProductList";
import { Button, HSpacer, IconButton, Text, VSpacer } from "@/components/DesignSystem";
import { CircularProgress, Container, Stack } from "@mui/material";
import { useQuery } from "react-query";
import { QueryKeys } from "@/constants/QueryKeys";
import { ManufacturerApi } from "@/utilities/api/ManufacturerApi";
import CloseIcon from "@mui/icons-material/Close";
import { AddedProductsSideSheet } from "@/pages/Promotions/PromotionModal/AddedProductsSideSheet";
import { RetailerAvatar } from "@/components/DesignSystem/Avatar/RetailerAvatar";
import { useSnackbar } from "@/providers/GlobalSnackbarProvider";

interface WorkflowStep3Props {
  productIdsWithPointsAwarded?: string[],
  onPromotionChanged: (promotion: PromotionInputs) => void,
  productType: 'earn' | 'redeem',
  promotion: PromotionInputs,
  selectedRetailer: ApiRetailer,
}

export const WorkflowStep3 = ({
  productIdsWithPointsAwarded,
  onPromotionChanged,
  productType,
  promotion,
  selectedRetailer,
}: WorkflowStep3Props) => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const [showAddedProductsSideSheet, setShowAddedProductsSideSheet] = useState(false);
  const [showHelpOption, setShowHelpOption] = useState(
    !promotion.productIdsForRedeemingPoints?.length,
  );

  const { data: selectedManufacturer, isFetching: isFetchingManufacturer } = useQuery(
    [QueryKeys.GET_MANUFACTURER_BY_ID, promotion.manufacturerId],
    () => promotion.manufacturerId ?
      ManufacturerApi.get(promotion.manufacturerId) : null,
    { enabled: !!promotion.manufacturerId },
  );


  const productIds = productType === 'earn'
    ? promotion.productIdsForEarningPoints ?? []
    : promotion.productIdsForRedeemingPoints ?? [];

  const updateProducts = (productIds: string[]) => {
    if (productType === 'earn') {
      const blockedRemoval = productIdsWithPointsAwarded?.find((productId) => (
        promotion.productIdsForEarningPoints?.includes(productId)
        && !productIds.includes(productId)
      ));
      if (blockedRemoval) {
        openSnackbar(t($ => $.promotions.create.step3.pointsAlreadyAwarded));
        return;
      }
      onPromotionChanged({
        ...promotion,
        productIdsForEarningPoints: productIds.length === 0 ? undefined : productIds,
      });
    } else {
      onPromotionChanged({
        ...promotion,
        productIdsForRedeemingPoints: productIds,
      });
    }
  };

  const totalEarningProducts = promotion.productIdsForEarningPoints?.length ?? 0;

  if (isFetchingManufacturer) {
    return <Container>
      <Stack alignItems='center'>
        <VSpacer size='14' />
        <CircularProgress />
      </Stack>
    </Container>;
  }

  return <>
    <Stack alignItems="center" direction="row" justifyContent="center">
      <RetailerAvatar retailer={selectedRetailer} size={20} />
      <HSpacer size="3" />
      <Text category="title-small">{selectedRetailer.name}:</Text>
      <HSpacer size="2" />
      <Text category="body-medium">{selectedManufacturer
        ? `All ${selectedManufacturer.name} products`
        : "All products"
      }</Text>
    </Stack>
    {showHelpOption && productType === 'redeem' && (
      <Stack sx={{ "margin": "0px 24px" }}>
        <VSpacer size="7" />
        <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{
          backgroundColor: "white",
          padding: "8px 16px",
        }}>
          <Text category="title-small" sx={{ "color": "#141414" }}>
            {t($ => totalEarningProducts > 1
              ? $.promotions.create.step3.allEarnRedeemWithCount
              : $.promotions.create.step3.allEarnRedeem, { count: totalEarningProducts })}
          </Text>
          <Stack direction="row">
            <Button
              onClick={() => setShowHelpOption(false)}
              square
              sx={{
                color: "white",
                backgroundColor: "#818B15",
                borderRadius: 1,
                flex: 1,
                width: 56,
              }}
              testID="show-help-option-no"
              variant="contained"
            >
              {t($ => $.promotions.create.step3.no)}
            </Button>
            <HSpacer size="5" />
            <Button
              onClick={() => {
                onPromotionChanged({
                  ...promotion,
                  productIdsForRedeemingPoints: Array.from(new Set([
                    ...promotion.productIdsForEarningPoints ?? [],
                    ...promotion.productIdsForRedeemingPoints ?? [],
                  ])),
                });
                setShowHelpOption(false);
              }}
              square
              sx={{
                color: "white",
                backgroundColor: "#818B15",
                borderRadius: 1,
                flex: 1,
                width: 56,
              }}
              testID="show-help-option-no"
              variant="contained"
            >
              {t($ => $.promotions.create.step3.yes)}
            </Button>
            <HSpacer size="5" />
            <IconButton
              onClick={() => setShowHelpOption(false)}
              sx={{ color: "black" }}
              testID="show-help-option-close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    )}
    <ProductList
      onAddedProductsButtonClicked={() => setShowAddedProductsSideSheet(true)}
      onChange={updateProducts}
      requiredUOM={productType === 'earn' ? promotion.uom : undefined}
      selectedIds={productIds}
      selectedRetailer={selectedRetailer}
    />
    {showAddedProductsSideSheet && (
      <AddedProductsSideSheet
        onChange={updateProducts}
        onClose={() => setShowAddedProductsSideSheet(false)}
        productIds={productIds}
        productType={productType}
      />
    )}
  </>;
};
